import React, { useState } from 'react';
import { X, AlertTriangle, Clock, Power } from 'lucide-react';
import { maintenanceService } from '../../services/maintenanceService';
import { useMaintenanceCheck } from '../../hooks/useMaintenanceCheck';

interface MaintenanceAdminModalProps {
    isOpen: boolean;
    onClose: () => void;
}

const COUNTDOWN_OPTIONS = [
    { value: 5, label: '5 minutter' },
    { value: 10, label: '10 minutter' },
    { value: 15, label: '15 minutter' },
    { value: 30, label: '30 minutter' },
];

export const MaintenanceAdminModal: React.FC<MaintenanceAdminModalProps> = ({ isOpen, onClose }) => {
    const { status, secondsRemaining, deactivate, refresh } = useMaintenanceCheck();
    const [countdownMinutes, setCountdownMinutes] = useState(10);
    const [message, setMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    if (!isOpen) return null;

    const isCurrentlyScheduled = status?.isScheduled ?? false;

    const handleActivate = async () => {
        setLoading(true);
        setError(null);
        try {
            await maintenanceService.activate({ countdownMinutes, message: message || undefined });
            await refresh();
            setMessage('');
        } catch (err: any) {
            setError(err.message || 'Feil ved aktivering');
        } finally {
            setLoading(false);
        }
    };

    const handleDeactivate = async () => {
        setLoading(true);
        setError(null);
        try {
            await deactivate();
        } catch (err: any) {
            setError(err.message || 'Feil ved deaktivering');
        } finally {
            setLoading(false);
        }
    };

    const formatCountdown = (s: number) => {
        const m = Math.floor(s / 60);
        const sec = s % 60;
        return `${m}:${sec.toString().padStart(2, '0')}`;
    };

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
                {/* Header */}
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
                    <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                        <AlertTriangle size={20} className="text-amber-500" />
                        Vedlikeholdsmodus
                    </h2>
                    <button
                        onClick={onClose}
                        className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                        <X size={20} className="text-gray-500" />
                    </button>
                </div>

                {/* Body */}
                <div className="px-5 py-4 space-y-4">
                    {isCurrentlyScheduled ? (
                        /* Aktiv status */
                        <div className="space-y-3">
                            <div className={`p-4 rounded-lg ${secondsRemaining !== null && secondsRemaining <= 0 ? 'bg-red-50 border border-red-200' : 'bg-amber-50 border border-amber-200'}`}>
                                <div className="flex items-center gap-2 mb-2">
                                    <Clock size={16} className={secondsRemaining !== null && secondsRemaining <= 0 ? 'text-red-600' : 'text-amber-600'} />
                                    <span className={`font-medium ${secondsRemaining !== null && secondsRemaining <= 0 ? 'text-red-800' : 'text-amber-800'}`}>
                                        {secondsRemaining !== null && secondsRemaining <= 0
                                            ? 'Vedlikehold er aktivt'
                                            : `Starter om ${formatCountdown(secondsRemaining ?? 0)}`
                                        }
                                    </span>
                                </div>
                                {status?.message && (
                                    <p className="text-sm text-gray-700">Melding: {status.message}</p>
                                )}
                                {status?.activatedBy && (
                                    <p className="text-xs text-gray-500 mt-1">Aktivert av: {status.activatedBy}</p>
                                )}
                            </div>

                            <button
                                onClick={handleDeactivate}
                                disabled={loading}
                                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors font-medium"
                            >
                                <Power size={16} />
                                {loading ? 'Avbryter...' : 'Avbryt vedlikehold'}
                            </button>
                        </div>
                    ) : (
                        /* Aktiverings-skjema */
                        <div className="space-y-4">
                            <p className="text-sm text-gray-600">
                                Alle påloggede brukere vil se et varselbanner med nedtelling.
                                Etter nedtellingen vises en melding om at vedlikehold pågår.
                            </p>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Nedtelling
                                </label>
                                <select
                                    value={countdownMinutes}
                                    onChange={(e) => setCountdownMinutes(Number(e.target.value))}
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                                >
                                    {COUNTDOWN_OPTIONS.map(opt => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Melding til brukerne (valgfritt)
                                </label>
                                <input
                                    type="text"
                                    value={message}
                                    onChange={(e) => setMessage(e.target.value)}
                                    placeholder="F.eks. Oppgradering til v2.5"
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                                    maxLength={200}
                                />
                            </div>

                            <button
                                onClick={handleActivate}
                                disabled={loading}
                                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-50 transition-colors font-medium"
                            >
                                <AlertTriangle size={16} />
                                {loading ? 'Aktiverer...' : 'Aktiver vedlikeholdsmodus'}
                            </button>
                        </div>
                    )}

                    {error && (
                        <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">{error}</p>
                    )}
                </div>
            </div>
        </div>
    );
};
